import React, { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-apollo'
import { useHistory } from 'react-router-dom'

import { AcornsIcon } from '@acorns/icons'
import {
  ConfirmationModal,
  ModalActionTypes,
  Spinner,
} from '@acorns/web-components'
import { useAnalyticsClient } from '@acorns/web-utils'
import styled from 'styled-components'

import { ModalAction } from 'components/ConfirmationModal'
import FullscreenTakeover from 'components/FullscreenTakeover'
import { Query, TierKey, TierSubscription } from 'generated/types'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { CloseAccountWrapper } from 'pages/close-account-v3/closeAccountWrapper'
import LoadingScreen from 'pages/close-account-v3/closureLoading'
import {
  CircularIcon,
  StyledBody,
  StyledCard,
  StyledCloseAccountButton,
  StyledHeading,
} from 'pages/close-account-v3/components'
import cancelInvestmentAccountRecurringInvestmentMutation from 'pages/close-account-v3/mutations/cancelInvestmentAccountRecurringInvestment.gql'
import cancelSpendRecurringMutation from 'pages/close-account-v3/mutations/cancelSpendRecurring.gql'
import supportTicketMutation from 'pages/close-account-v3/mutations/mutation.gql'
import laterLiquidationMutation from 'pages/close-account-v3/mutations/withdrawBalance.gql'
import userQuery from 'pages/close-account-v3/queries/userQuery.gql'
import { useEdgeCaseProductInfo } from 'pages/settings/my-subscription/tier-subscription-center/utils'
import { style } from 'theme'

import userTierQuery from '../queries/user-tier-query.gql'
import { AccountState } from '../tier-subscription-center/index'
import { ConfirmationScreen } from './confirmation-screen'
import {
  ConfirmDowngradeClosureCTA,
  ConfirmDowngradeClosureCancelCTA,
  ConfirmDowngradeClosureConfirmCTA,
  ConfirmDowngradeViewed,
  DowngradeDoneCTA,
  DowngradeDoneViewed,
  DowngradeStartCTA,
  DowngradeStartViewed,
} from './segment'
import { RequestScreen } from './with-balance-request-screen'

type HandlerProps = {
  handleGetStartedCTAPress: () => void
  handleCancel: () => void
  handleCancelSubscriptionCTAPress: () => void
  handleDone: () => void
  handleShowConfirmAccountClosureModal: () => void
}

type RecurringInvestments = {
  spend: {
    isOn: boolean
    id: string
  }
  later: {
    isOn: boolean
    id: string
  }
}

type UserData = {
  uuid: string
  email: string
  firstName: string
  lastName: string
  phoneNumber: number
  dob: string
  fundingSource: {
    name: string
    last4: number
    fundingType: string
    type: string
  }
}

type Props = {
  userData: UserData | undefined
  recurringInvestments: RecurringInvestments | undefined
  loading: boolean
  modalActions: ModalAction[]
  modalBody: string
  modalHeader: string
  location: Location
  userAccountState: AccountState
  balances: { product: string; amount: number }[]
  hasBalance: boolean
  isSpendClosureRequired: boolean | undefined
  isLaterClosureRequired: boolean | undefined
  spendBalance: number | undefined
  laterBalance: number | undefined
  shouldDisplaySpendBalance: boolean
  shouldDisplayLaterBalance: boolean
  hasSpendAccount: boolean
  hasLaterAccount: boolean
  isWebView: boolean
  shouldShowClosureConfirmationScreen: boolean
  shouldShowCloseAccountConfirmationModal: boolean
  setShouldShowCloseAccountConfirmationModal: (
    shouldShowCloseAccountConfirmationModal: boolean,
  ) => void
}

export type MergedProps = HandlerProps & Props

const Loader = styled.div`
  align-items: center;
  display: flex;
  height: 100vh;
`
const SlateNumberedList = styled.ol`
  color: ${style((theme) => theme.colors.white)};
  counter-reset: customCounter;
  font-size: 14px;
  list-style: none;
  padding-left: 40px;
  margin: 24px;

  & li {
    counter-increment: customCounter;
    line-height: 25px;
    margin-bottom: 0.75rem;
    position: relative;
    color: ${style((theme) => theme.colors.slate)};
    text-align: left;
    &::before {
      background-color: ${style((theme) => theme.colors.slate)};
      border-radius: 50%;
      color: ${style((theme) => theme.colors.white)};
      content: counter(customCounter);
      font-size: 10px;
      font-weight: bold;
      left: calc(-1 * 25px - 10px);
      line-height: 20px;
      position: absolute;
      text-align: center;
      top: 12%;
      width: 20px;
    }
  }
  @media screen and (max-width: 320px) {
    margin: 5px;
  }
`
const ListWrapper = styled.div`
  border: 1px solid rgb(245, 245, 245);
  border-radius: 10px;
  margin: 0 auto;
  &&& {
    margin-bottom: 30px;
  }
  max-width: 335px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin: 0;
  }
  @media screen and (max-width: 320px) {
    width: auto;
    margin-bottom: 0px;
  }
`

const ButtonContainer = styled.div`
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin-top: auto;
    border-top: none;
    position: relative;
    width: 100%;
  }
  @media screen and (max-width: 320px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }
`

const setClosureItems = (hasRecurringOn: boolean, hasBalances: boolean) => {
  const closureItems = []
  if (hasRecurringOn) {
    closureItems.push('Turning off transfers')
  }

  if (hasBalances) {
    closureItems.push('Withdrawing balances')
  }

  closureItems.push('Requesting closure')
  return closureItems
}

const isClosureRequired = (
  tierDowngrade: string | null,
  closedProduct: string,
  products: any,
  userAccountState: any,
) => {
  if (userAccountState) {
    return tierDowngrade === TierKey.Bronze && userAccountState
      ? products.length > 0
        ? products?.includes(closedProduct)
        : userAccountState?.spendAccount?.active
      : undefined
  }
  return undefined
}

const renderListItems = (items: string[]) => {
  return items.map((item) => {
    return <li key={item}>{item}</li>
  })
}

const getSpendBalance = (userAccountState: any) => {
  if (userAccountState) {
    return userAccountState?.spendAccount?.balance ?? -1
  }
  return undefined
}
const getRecurringInvestments = (
  userAccountState: any,
): RecurringInvestments | undefined => {
  if (userAccountState) {
    return {
      spend: {
        isOn: userAccountState?.spendAccount?.hasRecurringTransferOn,
        id: userAccountState?.spendAccount?.recurringTransferId,
      },
      later: {
        isOn:
          (userAccountState?.laterAccount?.recurringInvestmentSettings
            ?.amount ?? -1) > 0,
        id: userAccountState?.laterAccount?.id,
      },
    }
  }
  return undefined
}

const getAccountBalances = (
  hasSpendAccount: boolean,
  hasLaterAccount: boolean,
  spend: number,
  later: number,
) => {
  const balanceArr = []
  if (hasSpendAccount) {
    balanceArr.push({ product: 'Spend', amount: spend > 0 ? spend : 0 })
  }
  if (hasLaterAccount) {
    balanceArr.push({ product: 'Later', amount: later > 0 ? later : 0 })
  }

  return balanceArr
}

const CardComponent = styled(StyledCard)<{ innerHeight?: number }>`
  height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
  &&& {
    border-radius: 0;
  }
`

const CombinedClosure: React.FC<MergedProps> = () => {
  const history = useHistory()
  const analytics = useAnalyticsClient()
  const [
    shouldShowCloseAccountConfirmationModal,
    setShouldShowCloseAccountConfirmationModal,
  ] = useState(false)
  const [shouldShowLoadingScreen, setShouldShowLoadingScreen] = useState(false)
  const [
    shouldShowClosureRequestedScreen,
    setShouldShowClosureRequestedScreen,
  ] = useState(false)
  const [shouldShowRequestScreen, setShouldShowRequestScreen] = useState(false)
  const [showContactSupportModal, setShowContactSupportModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const userAccountState: any = useEdgeCaseProductInfo()
  const { height } = useWindowDimensions()

  useEffect(() => {
    if (userAccountState) {
      setLoading(false)
    }
  }, [userAccountState])

  const { data: userData }: { data: UserData | undefined } = useQuery(userQuery)
  const { data: tierInfo }: { data: Query | undefined } =
    useQuery(userTierQuery)
  const [laterTotalLiquidationMutation] = useMutation(laterLiquidationMutation)
  const [createSupportTicketMutation] = useMutation(supportTicketMutation)
  const [stopSpendRecurringMutation] = useMutation(cancelSpendRecurringMutation)
  const [stopInvestmentAccountRecurringMutation] = useMutation(
    cancelInvestmentAccountRecurringInvestmentMutation,
  )

  const productsToClose = history.location?.state?.productsToClose
  const products = productsToClose?.map(({ key }: { key: string }) => key) || []
  const tierRequestedForDowngrade = sessionStorage.getItem(
    'requestedTierDowngrade',
  )
  const isWebView = window.location.pathname.startsWith('/wv/')
  const currentTier = tierInfo?.tierSubscriptionForUser as TierSubscription
  const listItemsForDowngradeSteps = []
  const liquidationData = history.location?.state?.liquidationData
  const laterBalance = liquidationData?.amount
  const isSpendClosureRequired = isClosureRequired(
    tierRequestedForDowngrade,
    'CLOSE_SPEND_ACCOUNT',
    products,
    userAccountState,
  )
  const isLaterClosureRequired = isClosureRequired(
    tierRequestedForDowngrade,
    'CLOSE_LATER_ACCOUNT',
    products,
    userAccountState,
  )
  const recurringInvestments = getRecurringInvestments(userAccountState)
  let spendBalance
  let hasLaterBalance: boolean
  const hasRecurringSettingsOn =
    (recurringInvestments?.spend?.isOn ?? false) ||
    (recurringInvestments?.later?.isOn ?? false)

  const shouldShowClosureConfirmationScreen =
    history.location?.state?.shouldShowClosureConfirmationScreen ||
    shouldShowRequestScreen

  if (isSpendClosureRequired) {
    spendBalance = getSpendBalance(userAccountState)
    listItemsForDowngradeSteps.push('Close your Spend account')
    sessionStorage.setItem('spendClosureRequired', 'true')
  }

  if (isLaterClosureRequired) {
    hasLaterBalance = (userAccountState?.laterAccount?.balance ?? -1) > 0
    listItemsForDowngradeSteps.push('Close your Later account')
    sessionStorage.setItem('laterClosureRequired', 'true')
  }
  listItemsForDowngradeSteps.push('Update your subscription plan')

  const balances = getAccountBalances(
    isSpendClosureRequired,
    isLaterClosureRequired,
    spendBalance,
    laterBalance,
  )

  if (
    shouldShowClosureConfirmationScreen &&
    !shouldShowClosureRequestedScreen
  ) {
    analytics.track('Screen Viewed', ConfirmDowngradeViewed)
  } else {
    analytics.track('Screen Viewed', DowngradeStartViewed)
  }

  const hasBalance = (spendBalance ?? -1) > 0 || (laterBalance ?? -1) > 0
  const closureItems = setClosureItems(hasRecurringSettingsOn, hasBalance)

  const exitWebViewSuccess = () =>
    window.location.assign('acorns://exit?navigate_to=present')

  const handleGetStartedCTAPress = () => {
    analytics.track('Button Tapped', DowngradeStartCTA)
    const stateProps = {
      referrer: history.location.pathname,
      hasLaterAccount: isLaterClosureRequired,
      hasSpendAccount: isSpendClosureRequired,
      hasLaterBalance: isLaterClosureRequired && hasLaterBalance,
      isDowngradeSubscriptionFlow: true,
    }

    if (isSpendClosureRequired) {
      return isWebView
        ? history.push('/wv/close-account/spend', stateProps)
        : history.push('/close-account/spend', stateProps)
    } else if (hasLaterBalance && isLaterClosureRequired) {
      return isWebView
        ? history.push('/wv/close-account/later', stateProps)
        : history.push('/close-account/later', stateProps)
    } else {
      setShouldShowRequestScreen(true)
    }
  }

  const handleCancel = () => {
    isWebView
      ? history.push('/wv/my-subscription')
      : history.push('/settings/my-subscription')
  }

  const handleCloseConfirmationModal = () => {
    analytics.track('Button Tapped', ConfirmDowngradeClosureCancelCTA)
    setShouldShowCloseAccountConfirmationModal(false)
  }

  const handleShowConfirmAccountClosureModal = () => {
    analytics.track('Button Tapped', ConfirmDowngradeClosureCTA)
    setShouldShowCloseAccountConfirmationModal(true)
  }

  const handleCancelSubscriptionCTAPress = () => {
    analytics.track('Button Tapped', ConfirmDowngradeClosureConfirmCTA)
    setShouldShowLoadingScreen(true)

    // @note Unfortunately this is hardcoded as a safe default for now since this component
    // was implemented outside the context of the selected Tier to downgrade to.
    const guessRequestedPrice = (currentPrice: number | null): number => {
      switch (currentPrice) {
        case 5:
          return 3
        case 3:
        default:
          return 1
      }
    }

    const tierPrices = {
      current: `$${currentTier.tierPrice?.amount?.value}`,
      requested:
        '$' + guessRequestedPrice(currentTier.tierPrice?.amount?.value),
    }

    const tagsForTicket = [
      'automatic_close_my_account',
      'account_management',
      `automatic_downgrade_my_acorns_account_${tierPrices.current}_to_${tierPrices.requested}`,
    ]
    if (isLaterClosureRequired) {
      tagsForTicket.push('automatic_close_my_acorns_later_account')
    }
    if (isSpendClosureRequired) {
      tagsForTicket.push('automatic_close_my_acorns_spend_account')
    }
    const userCurrentTier = currentTier?.tier?.key
    const requestedTier =
      isLaterClosureRequired || isSpendClosureRequired
        ? TierKey.Bronze
        : TierKey.Silver

    const spendAccountMessage =
      userAccountState?.spendAccount?.active &&
      userAccountState?.spendAccount?.bankAccountStatus !== open
        ? '--please cancel pending Spend card order.'
        : ''

    const input = {
      category: 'Close My Account',
      uuid: userData?.uuid,
      email: userData?.email,
      firstName: userData?.firstName,
      lastName: userData?.lastName,
      message: `Requesting Downgrade of my Acorns Account. Closure of my ${
        isLaterClosureRequired ? 'Later' : ''
      } ${
        isSpendClosureRequired ? 'Spend' : ''
      } account(s)${spendAccountMessage}.Reason: Downgrading Tier via App. `,
      phone: userData?.phoneNumber,
      subcategory: `Downgrade ${tierPrices.current} to ${tierPrices.requested}`,
      tags: tagsForTicket,
    }

    if (hasRecurringSettingsOn) {
      if (recurringInvestments?.later?.isOn) {
        stopInvestmentAccountRecurringMutation({
          variables: { investmentAccountId: recurringInvestments?.later?.id },
        })
      }

      if (recurringInvestments?.spend?.isOn) {
        stopSpendRecurringMutation({
          variables: {
            id: recurringInvestments?.spend?.id,
          },
        })
      }
    }

    if (laterBalance && liquidationData) {
      laterTotalLiquidationMutation({
        variables: {
          investmentAccountId: liquidationData.investmentAccountId,
          federalTaxes: liquidationData.federalTaxes,
          stateTaxes: liquidationData.stateTaxes,
          taxYear: liquidationData.taxYear,
          reason: liquidationData.reason,
        },
      })
        .then(() => {
          createSupportTicketMutation({
            variables: { input },
          }).then(() => {
            sessionStorage.clear()
            setShouldShowLoadingScreen(false)
            setShouldShowClosureRequestedScreen(true)
            analytics.track(
              'Screen Viewed',
              DowngradeDoneViewed(userCurrentTier, requestedTier),
            )
          })
        })
        .catch(() => {
          setShowContactSupportModal(true)
        })
    } else {
      createSupportTicketMutation({
        variables: { input },
      }).then(() => {
        sessionStorage.clear()
        setShouldShowLoadingScreen(false)
        setShouldShowClosureRequestedScreen(true)

        analytics.track(
          'Screen Viewed',
          DowngradeDoneViewed(userCurrentTier, requestedTier),
        )
      })
    }
  }

  const handleWithdrawError = () => {
    const url = isWebView ? '/wv/my-subscription' : '/settings/my-subscription'
    history.push(url)
  }

  const handleDone = () => {
    analytics.track('Button Tapped', DowngradeDoneCTA, () => {
      if (isWebView) {
        exitWebViewSuccess()
      } else {
        history.push('/present')
      }
    })
  }

  const getModalProps = () => {
    const modalActions: ModalAction[] = [
      {
        handler: handleCloseConfirmationModal,
        label: 'Never mind',
        type: ModalActionTypes.secondary,
      },
      {
        handler: handleCancelSubscriptionCTAPress,
        label: 'Continue',
        type: ModalActionTypes.danger,
      },
    ]
    const modalBody =
      "This means we'll sell any shares, transfer all your money into your linked checking account, and update your subscription."

    const modalHeader = 'Are you sure?'
    return {
      modalActions,
      modalBody,
      modalHeader,
    }
  }

  const getErrorModalProps = () => {
    const modalHeader = 'Contact support'
    const modalBody =
      'An error occurred. Please contact support to continue closing your account(s).'
    const modalActions = [
      {
        label: 'OK',
        type: ModalActionTypes.secondary,
        handler: () => handleWithdrawError,
      },
    ]
    return {
      modalActions,
      modalBody,
      modalHeader,
    }
  }

  const confirmationModalProps = getModalProps()
  const errorModalProps = getErrorModalProps()

  return (
    <>
      {loading && (
        <Loader>
          <Spinner />
        </Loader>
      )}
      {!loading &&
        shouldShowClosureRequestedScreen &&
        !shouldShowLoadingScreen && (
          <ConfirmationScreen
            handleDone={handleDone}
            isWebView={isWebView}
            hasBalance={hasBalance}
          />
        )}
      {!loading && !shouldShowClosureRequestedScreen && (
        <>
          {isWebView && (
            <FullscreenTakeover innerHeight={height}>
              <CloseAccountWrapper
                isWebView={isWebView}
                action={
                  !shouldShowLoadingScreen
                    ? { onClick: handleCancel, shouldShowCloseIcon: false }
                    : undefined
                }
                link={
                  !shouldShowLoadingScreen
                    ? { onClick: handleCancel, text: 'Cancel' }
                    : undefined
                }
                actionStripTitle={'Change subscription'}
                handleCancel={handleCancel}
                showCancellable={
                  !shouldShowClosureRequestedScreen && !shouldShowLoadingScreen
                }
                shouldNotShowBack={shouldShowLoadingScreen}
              >
                {!shouldShowClosureConfirmationScreen &&
                  !shouldShowClosureRequestedScreen && (
                    <CardComponent innerHeight={height} isWebView={isWebView}>
                      <CircularIcon>
                        <AcornsIcon
                          icon={AcornsIcon.Icon.AcornsLogo}
                          width="36px"
                        />
                      </CircularIcon>
                      <StyledHeading>
                        Let's finish your subscription change
                      </StyledHeading>
                      <StyledBody>
                        After confirming some information, we’ll take the
                        following actions to change your plan.
                      </StyledBody>
                      <ListWrapper>
                        <SlateNumberedList>
                          {renderListItems(listItemsForDowngradeSteps)}
                        </SlateNumberedList>
                      </ListWrapper>
                      <ButtonContainer>
                        <StyledCloseAccountButton
                          onPress={handleGetStartedCTAPress}
                        >
                          Get started
                        </StyledCloseAccountButton>
                      </ButtonContainer>
                    </CardComponent>
                  )}

                {shouldShowClosureConfirmationScreen &&
                  !shouldShowClosureRequestedScreen &&
                  !shouldShowLoadingScreen && (
                    <RequestScreen
                      isWebView={isWebView}
                      hasBalance={hasBalance}
                      userData={userData}
                      setShouldShowCloseAccountConfirmationModal={
                        setShouldShowCloseAccountConfirmationModal
                      }
                      shouldShowCloseAccountConfirmationModal={
                        shouldShowCloseAccountConfirmationModal
                      }
                      handleShowConfirmAccountClosureModal={
                        handleShowConfirmAccountClosureModal
                      }
                      recurringInvestments={recurringInvestments}
                      balances={balances}
                      loading={loading}
                      modalActions={confirmationModalProps.modalActions}
                      modalBody={confirmationModalProps.modalBody}
                      modalHeader={confirmationModalProps.modalHeader}
                      handleCancelSubscriptionCTAPress={
                        handleCancelSubscriptionCTAPress
                      }
                    />
                  )}

                {shouldShowLoadingScreen && (
                  <LoadingScreen
                    closureItems={closureItems}
                    closureHeading="Requesting account closures"
                    isVisible={shouldShowLoadingScreen}
                    isWebView={isWebView}
                  />
                )}
              </CloseAccountWrapper>
            </FullscreenTakeover>
          )}
          {!isWebView && (
            <CloseAccountWrapper
              handleCancel={handleCancel}
              closeIcon={false}
              actionStripTitle={
                !shouldShowClosureConfirmationScreen &&
                !shouldShowClosureRequestedScreen
                  ? 'Change subscription'
                  : undefined
              }
              showCancellable={
                shouldShowClosureRequestedScreen || shouldShowLoadingScreen
                  ? false
                  : true
              }
              showBack={
                shouldShowClosureRequestedScreen || shouldShowLoadingScreen
                  ? false
                  : true
              }
            >
              {!shouldShowClosureConfirmationScreen &&
                !shouldShowClosureRequestedScreen && (
                  <StyledCard isWebView={isWebView}>
                    <CircularIcon>
                      <AcornsIcon
                        icon={AcornsIcon.Icon.AcornsLogo}
                        width="36px"
                      />
                    </CircularIcon>
                    <StyledHeading>Change your subscription</StyledHeading>
                    <StyledBody>
                      After confirming some information, we’ll take the
                      following actions to change your plan.
                    </StyledBody>
                    <ListWrapper>
                      <SlateNumberedList>
                        {renderListItems(listItemsForDowngradeSteps)}
                      </SlateNumberedList>
                    </ListWrapper>
                    <ButtonContainer>
                      <StyledCloseAccountButton
                        onPress={handleGetStartedCTAPress}
                      >
                        Get started
                      </StyledCloseAccountButton>
                    </ButtonContainer>
                  </StyledCard>
                )}

              {shouldShowClosureConfirmationScreen &&
                !shouldShowClosureRequestedScreen &&
                !shouldShowLoadingScreen && (
                  <RequestScreen
                    isWebView={isWebView}
                    hasBalance={hasBalance}
                    userData={userData}
                    setShouldShowCloseAccountConfirmationModal={
                      setShouldShowCloseAccountConfirmationModal
                    }
                    shouldShowCloseAccountConfirmationModal={
                      shouldShowCloseAccountConfirmationModal
                    }
                    handleShowConfirmAccountClosureModal={
                      handleShowConfirmAccountClosureModal
                    }
                    recurringInvestments={recurringInvestments}
                    balances={balances}
                    loading={loading}
                    modalActions={confirmationModalProps.modalActions}
                    modalBody={confirmationModalProps.modalBody}
                    modalHeader={confirmationModalProps.modalHeader}
                    handleCancelSubscriptionCTAPress={
                      handleCancelSubscriptionCTAPress
                    }
                  />
                )}

              {shouldShowLoadingScreen && (
                <LoadingScreen
                  closureItems={closureItems}
                  closureHeading="Requesting account closures"
                  isVisible={shouldShowLoadingScreen}
                  isWebView={isWebView}
                />
              )}
            </CloseAccountWrapper>
          )}
        </>
      )}
      {!loading && showContactSupportModal && (
        <ConfirmationModal
          message={errorModalProps.modalBody}
          title={errorModalProps.modalHeader}
          actions={errorModalProps.modalActions}
          isOpen={showContactSupportModal}
          onRequestClose={() => setShowContactSupportModal(false)}
        />
      )}
    </>
  )
}

export default CombinedClosure
